import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "../components/Icon.js";
import { ThemeToggle } from "../components/ThemeToggle.js";
import { acceptInvite, ApiError, getInvitation } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

type Status =
  | { kind: "loading" }
  | { kind: "ready"; email: string; name: string | null }
  | { kind: "missing" }
  | { kind: "expired" }
  | { kind: "used" };

export function AcceptInvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getInvitation(token)
      .then((inv) => {
        if (!active) return;
        setStatus({ kind: "ready", email: inv.email, name: inv.name });
        setName(inv.name ?? "");
      })
      .catch((err) => {
        if (!active) return;
        if (err instanceof ApiError) {
          if (err.status === 404) setStatus({ kind: "missing" });
          else if (err.code === "invitation_expired") setStatus({ kind: "expired" });
          else if (err.code === "invitation_already_used") setStatus({ kind: "used" });
          else setStatus({ kind: "missing" });
        } else {
          setStatus({ kind: "missing" });
        }
      });
    return () => {
      active = false;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvite(token, password, name.trim() || undefined);
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "weak_password") setError(err.message ?? "Password is too weak.");
        else if (err.code === "invitation_expired") setError("This invitation has expired. Ask your admin for a new one.");
        else if (err.code === "invitation_already_used") setError("This invitation has already been used.");
        else setError(err.message ?? "Couldn't accept invitation.");
      } else {
        setError(err instanceof Error ? err.message : "Couldn't accept invitation.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-radial-deep min-h-screen flex flex-col items-center justify-center relative">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      <main className="w-full max-w-md px-6 py-12 flex-grow flex items-center justify-center">
        <div className="glass-card-elevated w-full rounded-xl p-8 space-y-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary-container to-transparent opacity-50" />
          <Header />

          {status.kind === "loading" && <SkeletonForm />}

          {status.kind === "missing" && (
            <Notice
              icon="error"
              tone="error"
              title="Invitation not found"
              body="This link doesn't match an active invitation. Ask your admin to send a new one."
            />
          )}

          {status.kind === "expired" && (
            <Notice
              icon="schedule"
              tone="warn"
              title="Invitation expired"
              body="Invitations are valid for 7 days. Ask your admin to issue a fresh one."
            />
          )}

          {status.kind === "used" && (
            <Notice
              icon="check_circle"
              tone="info"
              title="Already used"
              body="This invitation has already been redeemed. Sign in with the password you set."
              cta={{ label: "Go to sign-in", to: "/login" }}
            />
          )}

          {status.kind === "ready" && (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="bg-surface-container-low border border-outline-variant rounded-lg p-3 text-body-sm">
                <p className="text-on-surface-variant text-label-xs font-mono uppercase tracking-widest mb-1">
                  Setting up
                </p>
                <p className="text-on-surface font-mono">{status.email}</p>
              </div>
              <Field
                id="name"
                label="Your name"
                icon="person"
                placeholder="Your full name"
                value={name}
                onChange={setName}
              />
              <Field
                id="password"
                label="Choose a password (12+ chars, letter + digit)"
                icon="lock"
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={setPassword}
                required
                autoComplete="new-password"
              />
              <Field
                id="confirm"
                label="Confirm password"
                icon="lock_reset"
                type="password"
                placeholder="••••••••••••"
                value={confirm}
                onChange={setConfirm}
                required
                autoComplete="new-password"
              />
              {error && (
                <div className="bg-error-container/20 border border-error/30 text-error rounded-lg p-3 text-body-sm flex items-start gap-2">
                  <Icon name="error" className="text-[18px] mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary-container text-on-primary-container text-body-base font-semibold py-3 rounded-lg hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-container/10 disabled:opacity-60"
              >
                {submitting ? "Creating account…" : "Create account"}
                <Icon name="arrow_forward" className="text-[18px]" />
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function Header() {
  return (
    <div className="text-center space-y-2">
      <div className="inline-flex items-center justify-center space-x-2 text-primary-container mb-2">
        <Icon name="sync" className="text-3xl" />
        <h1 className="text-xl font-black tracking-tighter uppercase">AppsDoCalSync</h1>
      </div>
      <h2 className="text-headline-md text-on-surface tracking-tight">Accept invitation</h2>
    </div>
  );
}

function SkeletonForm() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-12 bg-surface-container-low rounded" />
      <div className="h-12 bg-surface-container-low rounded" />
      <div className="h-12 bg-surface-container-low rounded" />
    </div>
  );
}

function Notice({
  icon,
  tone,
  title,
  body,
  cta,
}: {
  icon: string;
  tone: "error" | "warn" | "info";
  title: string;
  body: string;
  cta?: { label: string; to: string };
}) {
  const colour =
    tone === "error" ? "text-error" : tone === "warn" ? "text-tertiary-container" : "text-primary-fixed-dim";
  const navigate = useNavigate();
  return (
    <div className="space-y-4 text-center">
      <Icon name={icon} className={`${colour}`} style={{ fontSize: "48px" }} />
      <h3 className="text-headline-md text-on-surface">{title}</h3>
      <p className="text-body-sm text-on-surface-variant">{body}</p>
      {cta && (
        <button
          onClick={() => navigate(cta.to)}
          className="px-4 py-2 bg-primary-container text-on-primary-container rounded-lg text-label-xs font-mono uppercase tracking-widest"
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}

function Field({
  id,
  label,
  icon,
  type = "text",
  placeholder,
  value,
  onChange,
  required,
  autoComplete,
}: {
  id: string;
  label: string;
  icon: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-label-xs text-outline uppercase tracking-widest font-mono px-1">
        {label}
      </label>
      <div className="relative group">
        <Icon
          name={icon}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary-container transition-colors"
        />
        <input
          id={id}
          name={id}
          type={type}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg py-2.5 pl-10 pr-4 text-on-surface text-body-sm placeholder:text-outline/50 focus:outline-none focus:border-primary-container input-glow transition-all"
        />
      </div>
    </div>
  );
}
